/**
 * CIRKLA lightweight live-update service.
 *
 * This is intentionally a small Flarum/Mithril service, not React/Redux.
 * It refreshes server state in the background and then asks existing CIRKLA
 * DOM sync helpers to update unread dots and small UI indicators.
 */

const DEFAULT_INTERVAL_MS = 5000;

let liveUpdateTimer = null;
let liveUpdateBusy = false;
let latestDiscussionSignature = "";
let liveUpdateBanner = null;
const LOCAL_POST_SUPPRESS_WINDOW_MS = 15000;

let lastLocalDiscussionWriteAt = 0;

function normalizeApiUrl(app, path) {
  const apiUrl = String(app.forum?.attribute?.("apiUrl") || "").replace(
    /\/$/,
    "",
  );

  return `${apiUrl}${path}`;
}

function currentUserId(app) {
  const user = app.session?.user;

  try {
    return String(user?.id?.() || user?.data?.id || "");
  } catch (_error) {
    return "";
  }
}

function pushFlarumPayload(app, payload) {
  if (!payload) return;

  try {
    if (typeof app.store?.pushPayload === "function") {
      app.store.pushPayload(payload);
    }
  } catch (_error) {
    // Background updates must never break the page.
  }
}

function requestJson(app, options) {
  if (typeof app.request !== "function") {
    return Promise.resolve(null);
  }

  return app.request({ ...options, background: true }).catch(() => null);
}

function isCalendarPage() {
  if (
    typeof document !== "undefined" &&
    document.querySelector(".CalendarPage")
  ) {
    return true;
  }

  if (typeof window === "undefined") return false;

  return /^\/(?:events|calendar)(?:\/|$)/.test(window.location.pathname);
}

function cacheConversationMessages(app, conversationId, payload) {
  const id = String(conversationId || "").trim();
  if (!id || !Array.isArray(payload?.data)) return 0;

  if (!app.cache) app.cache = {};
  if (!app.cache.messages) app.cache.messages = [];
  if (!app.cache.messages[id]) app.cache.messages[id] = [];

  let added = 0;

  payload.data.forEach((item) => {
    const messageId = String(item?.id || "");
    if (!messageId) return;

    const alreadyCached = Boolean(app.cache.messages[id][messageId]);
    let record = null;
    try {
      record = app.store?.getById?.("messages", messageId);
    } catch (_error) {
      record = null;
    }

    if (!record && typeof app.store?.pushObject === "function") {
      try {
        record = app.store.pushObject(item);
      } catch (_error) {
        record = null;
      }
    }

    if (record) {
      app.cache.messages[id][messageId] = record;
      if (!alreadyCached) added += 1;
    }
  });

  return added;
}

function scrollActiveChatToBottom() {
  if (typeof document === "undefined") return;

  const scroll = () => {
    const history = document.querySelector(".chat-history");
    if (!history) return;

    const lastMessage = history.querySelector("li:last-child");

    history.scrollTop = Math.max(history.scrollHeight - history.clientHeight, 0);

    if (lastMessage instanceof HTMLElement) {
      lastMessage.scrollIntoView({
        block: "end",
        inline: "nearest",
        behavior: "auto",
      });
    }

    history.scrollTop = Math.max(history.scrollHeight - history.clientHeight, 0);
  };

  window.setTimeout(scroll, 50);
  window.setTimeout(scroll, 250);
  window.setTimeout(scroll, 700);
}

function cachedConversationIds(app) {
  const ids = new Set();

  try {
    const routeId = String(window?.m?.route?.param?.("id") || "").trim();
    if (routeId) ids.add(routeId);
  } catch (_error) {
    // The desktop conversation view often keeps selection outside the route.
  }

  const cached = app.cache?.messages;
  if (cached && typeof cached === "object") {
    Object.keys(cached).forEach((id) => {
      const normalized = String(id || "").trim();
      if (normalized && normalized !== "undefined" && normalized !== "null") {
        ids.add(normalized);
      }
    });
  }

  return Array.from(ids);
}

function refreshCurrentUser(app) {
  const userId = currentUserId(app);

  if (!userId || typeof app.store?.find !== "function") {
    return Promise.resolve(null);
  }

  /*
   * This refreshes attributes such as unreadMessages on app.session.user.
   */
  return app.store.find("users", userId).catch(() => null);
}

function refreshConversations(app, deps) {
  return requestJson(app, {
    method: "GET",
    url: normalizeApiUrl(app, "/neoncube-private-messages/conversations"),
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  }).then((payload) => {
    pushFlarumPayload(app, payload);
    deps.syncSidebarChatUnreadState();
    deps.syncConversationListUnreadState();

    return payload;
  });
}

function refreshActiveConversation(app, deps) {
  const path = String(window?.location?.pathname || "");

  if (!/\/conversations(?:\/|$)/.test(path)) {
    return Promise.resolve(null);
  }

  const ids = cachedConversationIds(app);
  if (!ids.length) {
    return Promise.resolve(null);
  }

  return Promise.all(
    ids.map((id) =>
      requestJson(app, {
        method: "GET",
        url: normalizeApiUrl(app, `/neoncube-private-messages/messages/${id}`),
        params: { offset: 0 },
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      }).then((payload) => {
        pushFlarumPayload(app, payload);
        const added = cacheConversationMessages(app, id, payload);

        return { payload, added };
      }),
    ),
  ).then((results) => {
    deps.syncSidebarChatUnreadState();
    deps.syncConversationListUnreadState();

    if (results.some((result) => result?.added > 0)) {
      scrollActiveChatToBottom();
    }

    return results.map((result) => result?.payload);
  });
}

function latestDiscussionSignatureFromPayload(payload) {
  const first = Array.isArray(payload?.data) ? payload.data[0] : null;

  if (!first) return "";

  const attrs = first.attributes || {};

  return [
    first.id || "",
    attrs.lastPostedAt || "",
    attrs.commentCount || "",
    attrs.lastPostNumber || "",
  ].join("|");
}

function removeLiveUpdateBanner() {
  if (liveUpdateBanner) {
    liveUpdateBanner.remove();
    liveUpdateBanner = null;
  }
}

function showLiveUpdateBanner(app, m) {
  if (typeof document === "undefined") return;
  if (liveUpdateBanner) return;

  const banner = document.createElement("button");
  banner.type = "button";
  banner.className = "CirklaLiveUpdateBanner";
  banner.textContent = app.translator.trans(
    "htwsaar-htw-theme.forum.liveUpdates.newPosts",
  );
  banner.setAttribute(
    "aria-label",
    app.translator.trans(
      "htwsaar-htw-theme.forum.liveUpdates.newPostsAria",
    ),
  );

  banner.addEventListener("click", (event) => {
    event.preventDefault();

    removeLiveUpdateBanner();

    /*
     * Use hard reload because m.route.set(currentPath) may not reload
     * the discussion list if the route is already active.
     */
    window.location.reload();
  });

  document.body.appendChild(banner);
  liveUpdateBanner = banner;
}

function discussionSeenStorageKey(app) {
  const userId = currentUserId(app) || "guest";

  return `cirkla.latestDiscussionSignature.${userId}`;
}

function latestDiscussionAuthorIdFromPayload(payload) {
  const first = Array.isArray(payload?.data) ? payload.data[0] : null;
  if (!first) return "";

  const relationships = first.relationships || {};

  const directAuthorId =
    relationships.lastPostedUser?.data?.id ||
    relationships.user?.data?.id ||
    "";

  if (directAuthorId) {
    return String(directAuthorId);
  }

  const included = Array.isArray(payload?.included) ? payload.included : [];
  const lastPostId = relationships.lastPost?.data?.id;

  if (!lastPostId) return "";

  const lastPost = included.find(
    (item) => item?.type === "posts" && String(item.id) === String(lastPostId),
  );

  return String(lastPost?.relationships?.user?.data?.id || "");
}

function refreshLatestDiscussionSignal(app, m) {
  return requestJson(app, {
    method: "GET",
    url: normalizeApiUrl(app, "/discussions"),
    params: {
      sort: "-lastPostedAt",
      "page[limit]": 1,
    },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  }).then((payload) => {
    const nextSignature = latestDiscussionSignatureFromPayload(payload);

    if (!nextSignature) return payload;

    const previousSignature = latestDiscussionSignature;
    latestDiscussionSignature = nextSignature;

    /*
     * First run only creates the baseline.
     * Do not show old posts as "new".
     */
    if (!previousSignature) {
      return payload;
    }

    if (nextSignature !== previousSignature) {
      /*
       * If this browser just created a discussion/post, do not show
       * "New posts available" to the same user. They already know.
       */
      if (recentlyCreatedLocalPost()) {
        return payload;
      }

      showLiveUpdateBanner(app, m);
    }

    return payload;
  });
}

function shouldSkipTick() {
  if (liveUpdateBusy) return true;

  /*
   * Keep polling even if the tab is not active.
   * Otherwise the second user may not detect new posts until focus changes.
   */
  return false;
}

function runLiveUpdateTick(app, m, deps) {
  if (shouldSkipTick()) return;

  liveUpdateBusy = true;

  Promise.all([
    refreshCurrentUser(app),
    refreshConversations(app, deps),
    refreshActiveConversation(app, deps),
    refreshLatestDiscussionSignal(app, m),
  ])
    .then(() => {
      deps.syncSidebarChatUnreadState();
      deps.syncConversationListUnreadState();

      // FullCalendar owns a stateful DOM tree. A global redraw caused by the
      // five-second background poll remounts that tree and looks like a page
      // reload, so let its own data source control updates on calendar routes.
      if (!isCalendarPage() && typeof m?.redraw === "function") {
        m.redraw();
      }
    })
    .finally(() => {
      liveUpdateBusy = false;
    });
}

function markLocalDiscussionWrite() {
  lastLocalDiscussionWriteAt = Date.now();
}

function recentlyCreatedLocalPost() {
  return (
    Date.now() - lastLocalDiscussionWriteAt < LOCAL_POST_SUPPRESS_WINDOW_MS
  );
}

function installLocalDiscussionWriteTracker(app) {
  if (!app || typeof app.request !== "function") return;
  if (app.__cirklaLocalDiscussionWriteTrackerInstalled) return;

  const originalRequest = app.request.bind(app);

  app.request = function (options = {}) {
    const method = String(options.method || "GET").toUpperCase();
    const url = String(options.url || "");

    const isWriteRequest = method === "POST" || method === "PATCH";
    const isDiscussionOrPostRequest =
      /\/api\/discussions(?:\/|$|\?)/.test(url) ||
      /\/api\/posts(?:\/|$|\?)/.test(url) ||
      /\/discussions(?:\/|$|\?)/.test(url) ||
      /\/posts(?:\/|$|\?)/.test(url);

    if (isWriteRequest && isDiscussionOrPostRequest) {
      markLocalDiscussionWrite();
    }

    return originalRequest.apply(this, arguments);
  };

  app.__cirklaLocalDiscussionWriteTrackerInstalled = true;
}

export function installCirklaLiveUpdates(app, m, deps, options = {}) {
  if (typeof window === "undefined") return;
  if (app.__cirklaLiveUpdatesInstalled) return;

  app.__cirklaLiveUpdatesInstalled = true;

  installLocalDiscussionWriteTracker(app);

  const intervalMs = Number(options.intervalMs || DEFAULT_INTERVAL_MS);

  const run = () => runLiveUpdateTick(app, m, deps);

  run();

  liveUpdateTimer = window.setInterval(run, intervalMs);

  window.addEventListener("focus", run, { passive: true });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      run();
    }
  });

  /*
   * Keep route changes clean. If the user manually refreshes the discussion
   * list, remove the "New posts available" banner.
   */
  if (
    m?.route &&
    typeof m.route.set === "function" &&
    !app.__cirklaLiveUpdatesRoutePatchInstalled
  ) {
    const originalRouteSet = m.route.set;

    m.route.set = function () {
      removeLiveUpdateBanner();

      const result = originalRouteSet.apply(this, arguments);

      setTimeout(run, 250);

      return result;
    };

    app.__cirklaLiveUpdatesRoutePatchInstalled = true;
  }
}

export function stopCirklaLiveUpdates(app) {
  if (typeof window !== "undefined" && liveUpdateTimer) {
    window.clearInterval(liveUpdateTimer);
  }

  liveUpdateTimer = null;
  liveUpdateBusy = false;
  removeLiveUpdateBanner();

  if (app) {
    app.__cirklaLiveUpdatesInstalled = false;
  }
}
